import { Link } from "react-router-dom";
import { Reveal, Divider, Overline } from "@/components/layout/Atoms";
import { ChefHat, Sparkles, Users, ArrowRight } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";
import { resolveMediaUrl } from "@/lib/api";
import Reviews from "@/components/Reviews";

const ICONS = { users: Users, chef: ChefHat, sparkles: Sparkles };

export default function Accueil() {
    const c = useContent();
    const a = c.accueil;

    return (
        <div data-testid="page-accueil">
            <section className="relative overflow-hidden">
                <div className="container-k grid lg:grid-cols-12 gap-12 lg:gap-16 items-center pt-10 pb-24 lg:pt-20 lg:pb-32">
                    <div className="lg:col-span-6 relative z-10">
                        <Reveal><Overline>{a.hero.overline}</Overline></Reveal>
                        <Reveal delay={120}>
                            <h1 className="mt-6 font-heading font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-kayama-ink text-balance">
                                {a.hero.h1_part1}<br />
                                <span className="italic text-kayama-pink">{a.hero.h1_italic}</span><br />
                                {a.hero.h1_part3}
                            </h1>
                        </Reveal>
                        <Reveal delay={240}>
                            <p className="script text-5xl md:text-6xl mt-6 leading-none">{a.hero.script}</p>
                        </Reveal>
                        <Reveal delay={360}>
                            <p className="mt-8 text-kayama-brown-2 text-lg max-w-md leading-relaxed">{a.hero.subtitle}</p>
                        </Reveal>
                        <Reveal delay={480}>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <Link to="/contact" data-testid="hero-cta-devis" className="btn-primary-k">
                                    {a.hero.cta_primary} <ArrowRight size={16} />
                                </Link>
                                <Link to="/menus" data-testid="hero-cta-menus" className="btn-ghost-k">{a.hero.cta_secondary}</Link>
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-6 relative">
                        <Reveal delay={200}>
                            <div className="relative">
                                <div className="absolute -inset-6 border border-kayama-gold/60 rounded-sm pointer-events-none" />
                                <div className="absolute -top-6 -left-6 w-12 h-12 border-t-2 border-l-2 border-kayama-pink" />
                                <div className="absolute -bottom-6 -right-6 w-12 h-12 border-b-2 border-r-2 border-kayama-pink" />
                                <img src={resolveMediaUrl(a.hero.image)} alt="Plat sénégalais raffiné" className="w-full h-[460px] lg:h-[600px] object-cover relative z-0" />
                                <div className="absolute -bottom-10 -left-10 bg-kayama-paper p-5 shadow-[0_10px_40px_rgba(62,39,35,0.08)] hidden md:block">
                                    <p className="font-heading text-3xl italic text-kayama-pink">{a.hero.badge_title}</p>
                                    <p className="overline">{a.hero.badge_subtitle}</p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
                <div className="absolute top-1/3 -left-32 w-96 h-96 rounded-full bg-kayama-pink/5 blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-kayama-gold/10 blur-3xl pointer-events-none" />
            </section>

            <section className="container-k pb-20">
                <div className="max-w-3xl mx-auto text-center">
                    <Divider />
                    <Overline>{a.intro.overline}</Overline>
                    <h2 className="mt-6 font-heading text-4xl md:text-5xl font-light text-kayama-brown leading-tight">
                        {a.intro.title_part1}<br /> {a.intro.title_part2} <span className="italic text-kayama-pink">{a.intro.italic}</span>{a.intro.title_part3}
                    </h2>
                    <p className="mt-8 text-kayama-brown-2 leading-relaxed text-lg">{a.intro.text}</p>
                </div>
            </section>

            <section className="container-k py-20" data-testid="services-preview">
                <div className="grid md:grid-cols-3 gap-10 lg:gap-14">
                    {a.services_preview.map((s, i) => {
                        const Icon = ICONS[s.icon] || Users;
                        return (
                            <Reveal delay={i * 120} key={i}>
                                <div className="group relative bg-kayama-paper border border-kayama-line hover:shadow-[0_20px_60px_rgba(62,39,35,0.10)] transition-all duration-500">
                                    <div className="overflow-hidden h-64">
                                        <img src={resolveMediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    </div>
                                    <div className="p-8">
                                        <div className="w-12 h-12 -mt-14 mb-4 bg-kayama-pink text-white flex items-center justify-center relative z-10 shadow-lg"><Icon size={22} /></div>
                                        <h3 className="font-heading text-2xl text-kayama-brown leading-tight">{s.title}</h3>
                                        <p className="mt-4 text-kayama-brown-2 leading-relaxed text-sm">{s.text}</p>
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            <section className="container-k py-24">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <img src={resolveMediaUrl(a.philosophy.image)} alt="Plat signature" className="w-full h-[520px] object-cover" />
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <Overline>{a.philosophy.overline}</Overline>
                        <h2 className="mt-6 font-heading text-4xl md:text-5xl font-light text-kayama-brown leading-tight">
                            {a.philosophy.title_part1} <span className="italic text-kayama-pink">{a.philosophy.italic}</span>
                        </h2>
                        <p className="mt-8 text-kayama-brown-2 leading-relaxed">{a.philosophy.text}</p>
                        {a.philosophy.stats?.length > 0 && (
                            <div className="mt-8 grid grid-cols-3 gap-6">
                                {a.philosophy.stats.map((m, i) => (
                                    <div key={i} className="border-t border-kayama-gold pt-4">
                                        <div className="font-heading text-4xl text-kayama-pink">{m.n}</div>
                                        <div className="overline mt-2">{m.l}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <Reviews />

            <section className="relative mt-16">
                <div className="relative h-[420px] overflow-hidden">
                    <img src={resolveMediaUrl(a.cta_banner.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-kayama-brown/75" />
                    <div className="relative container-k h-full flex flex-col items-center justify-center text-center">
                        <p className="script text-5xl md:text-6xl text-kayama-gold-light">{a.cta_banner.script}</p>
                        <h2 className="mt-4 font-heading text-3xl md:text-5xl text-kayama-cream font-light max-w-2xl">{a.cta_banner.title}</h2>
                        <Link to="/contact" data-testid="banner-cta-devis" className="mt-8 btn-primary-k">{a.cta_banner.cta} <ArrowRight size={16} /></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
